/**
 * THE COUNTER THIS USER'S ROLE ASSIGNS THEM TO, if the hospital has said.
 *
 * Desk, 2026-09-07: "keep main cash counter user should access only main cash
 * counter and no selection of counter."
 *
 * `CounterSelectionGate` asks every biller which desk they are at, and its own
 * header explains why it refuses to GUESS one — picking the first counter in the
 * branch would post a cashier's money to whichever desk sorted first. That is
 * still true. This is not a guess: `Role.defaultCounterId` is the hospital
 * stating, on the record, which desk a role stands at, and the server resolves it
 * from a foreign key rather than from a role NAME that anybody can rename.
 *
 * RESOLVED SERVER-SIDE, DELIBERATELY. The decoded token already carries the
 * caller's role names, so this could have matched "the role called Main Cash
 * Counter" against "the counter called Main Cash Counter" with no request at
 * all. That is precisely the trap `CounterSelectionGate` warns about elsewhere in
 * its own file: role names are tenant data, and a rename would silently re-pin a
 * cashier to the wrong desk — or to none.
 *
 * THROUGH THE GENERATED CLIENT, like every other call in this module. A first
 * version reached for `httpClient` directly because the route was not on the
 * generated surface yet — and `sprint-1-acceptance.test.ts` refused it, exactly
 * as it should: the rule is "no feature file calls httpClient, axios or fetch
 * directly", and the fix is to put the endpoint ON the contract rather than to
 * step around the gate. Registering the operation and regenerating was three
 * commands; a hand-rolled call would have been a second way to reach the API
 * that nothing typechecks.
 *
 * `retry: false` and a null fallback: a user without
 * `financial:payment:process` gets a 403 here, which is the ordinary state of
 * every clerk who never handles cash. Retrying it is noise, and "no assignment"
 * is the correct reading either way — they are asked, exactly as before.
 */
import { useQuery } from '@tanstack/react-query';
import { getAssignedCounter } from '../services/op-billing.service';
import { useAuthStore } from '@store/auth.store';

export interface AssignedCounter {
  counter: {
    id: string;
    code: string;
    name: string;
    branchId: string;
    /**
     * `MAIN_COUNTER` is legitimate HERE and nowhere else — an assigned counter
     * was not chosen from a list, so `isSelectableDesk`'s fence does not apply.
     * See `assigned-counter.queries.ts`.
     */
    counterType: string;
    isActive: boolean;
  } | null;
  /** Two roles naming two different desks. No answer is better than a coin-flip. */
  ambiguous: boolean;
  /** The role that supplied the answer, so a support call can name the cause. */
  viaRole: string | null;
}

const NONE: AssignedCounter = { counter: null, ambiguous: false, viaRole: null };

export interface UseAssignedCounter extends AssignedCounter {
  /** True while the answer is unknown — callers must not ask the operator yet. */
  isLoading: boolean;
}

export function useAssignedCounter(enabled = true): UseAssignedCounter {
  const userId = useAuthStore((state) => state.user?.id);

  const query = useQuery({
    queryKey: ['op-billing', 'assigned-counter', userId],
    queryFn: () => getAssignedCounter(),
    enabled: enabled && Boolean(userId),
    // A role assignment changes when an administrator edits a role, which is
    // not something that happens mid-shift.
    staleTime: 5 * 60_000,
    retry: false,
  });

  return {
    ...(query.data ?? NONE),
    /*
      A FAILED CALL IS NOT "STILL LOADING". `isPending` stays true forever on a
      query that will not retry after a 403, which would hold the sign-in gate
      shut for every clerk who does not handle cash. Settled-or-errored is the
      honest reading of "we know the answer now".
    */
    isLoading: query.isPending && !query.isError,
  };
}
